import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { trackEvent } from '../../../utils/analytics';
import { toBlob } from 'html-to-image';

import ChartShareFrame from './ChartShareFrame';
import {
  CHART_SHARE_PRESETS,
  DEFAULT_CHART_SHARE_PRESET_ID,
  buildChartShareFilename,
  getChartSharePreset,
} from './chartShareConfig';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const waitForFrameImages = async (node) => {
  const images = [...(node?.querySelectorAll?.('img') || [])];
  await Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve();
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }));
};

function ChartShareDialog({
  open,
  onClose,
  chartId,
  ticker,
  companyName,
  entity,
  buildEntity,
  initialTitle,
  initialSubtitle,
  sourceLabel = 'SoftwareIQ',
  metadata,
  buildMetadata,
  renderChart,
  renderChartControls,
  initialChartState = {},
  initialThemeMode,
  allowThemeSelection = true,
  activeFilterLabels = [],
}) {
  const appTheme = useTheme();
  const isNarrow = useMediaQuery(appTheme.breakpoints.down('sm'));
  const frameRef = useRef(null);
  const [presetId, setPresetId] = useState(DEFAULT_CHART_SHARE_PRESET_ID);
  const [themeMode, setThemeMode] = useState(initialThemeMode || appTheme.palette.mode || 'light');
  const [title, setTitle] = useState(initialTitle || 'Company Chart');
  const [subtitle, setSubtitle] = useState(initialSubtitle || '');
  const [chartState, setChartState] = useState(initialChartState);
  const [assetReady, setAssetReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const preset = getChartSharePreset(presetId);
  const titleValid = title.trim().length > 0;
  const filterMetadata = activeFilterLabels.join(' · ');
  const baseMetadata = buildMetadata ? buildMetadata(chartState) : metadata;
  const resolvedMetadata = [baseMetadata, filterMetadata].filter(Boolean).join(' · ');
  const resolvedEntity = buildEntity?.(chartState) || entity;
  const resolvedTicker = resolvedEntity?.ticker || ticker;
  const resolvedCompanyName = resolvedEntity?.label || companyName;
  const previewScale = useMemo(() => Math.min(
    (isNarrow ? 300 : 760) / preset.width,
    (isNarrow ? 390 : 520) / preset.height
  ), [isNarrow, preset.height, preset.width]);

  useEffect(() => {
    if (!open) return;
    setPresetId(DEFAULT_CHART_SHARE_PRESET_ID);
    setThemeMode(initialThemeMode || appTheme.palette.mode || 'light');
    setTitle(initialTitle || 'Company Chart');
    setSubtitle(initialSubtitle || '');
    setChartState(initialChartState);
    setError('');
    setExporting(false);
    trackEvent('share', { method: 'dialog_open', content_type: 'chart', item_id: chartId });
  }, [appTheme.palette.mode, chartId, initialChartState, initialSubtitle, initialThemeMode, initialTitle, open]);

  const handleDownload = useCallback(async () => {
    if (!frameRef.current || !titleValid || !assetReady) return;

    setExporting(true);
    setError('');
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      await waitForFrameImages(frameRef.current);
      const blob = await toBlob(frameRef.current, {
        width: preset.width,
        height: preset.height,
        canvasWidth: preset.width,
        canvasHeight: preset.height,
        pixelRatio: 1,
        cacheBust: true,
        backgroundColor: themeMode === 'dark' ? '#101522' : '#ffffff',
      });
      if (!blob) throw new Error('The image renderer returned an empty file.');

      const filename = buildChartShareFilename({
        ticker: resolvedTicker,
        companyName: resolvedCompanyName,
        entityId: resolvedEntity?.id || resolvedEntity?.ticker || resolvedEntity?.label,
        chartId,
        presetId,
      });
      downloadBlob(blob, filename);
      trackEvent('share', { method: 'image_export', content_type: 'chart', item_id: chartId, preset_id: presetId, theme_mode: themeMode });
    } catch (downloadError) {
      console.error('Chart image export failed', downloadError);
      trackEvent('exception', { description: 'chart_share_export_failed', fatal: false, item_id: chartId });
      setError('We could not create this image. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [assetReady, chartId, preset.height, preset.width, presetId, resolvedCompanyName, resolvedEntity, resolvedTicker, themeMode, titleValid]);

  return (
    <Dialog open={open} onClose={exporting ? undefined : onClose} maxWidth="lg" fullWidth aria-labelledby="chart-share-title">
      <DialogTitle id="chart-share-title">Export chart image</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'flex-start' }}>
          <Stack spacing={2} sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="chart-share-preset-label">View</InputLabel>
              <Select
                labelId="chart-share-preset-label"
                label="View"
                value={presetId}
                onChange={(event) => setPresetId(event.target.value)}
              >
                {CHART_SHARE_PRESETS.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label} ({option.dimensions})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {allowThemeSelection ? <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Background
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={themeMode}
                onChange={(event, value) => { if (value) setThemeMode(value); }}
                aria-label="Image background"
              >
                <ToggleButton value="light">Light</ToggleButton>
                <ToggleButton value="dark">Dark</ToggleButton>
              </ToggleButtonGroup>
            </Box> : null}

            {renderChartControls?.({ chartState, setChartState })}

            {activeFilterLabels.length > 0 ? (
              <Box data-testid="chart-share-active-filters">
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Active filters
                </Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {activeFilterLabels.map((filterLabel) => (
                    <Chip key={filterLabel} label={filterLabel} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            ) : null}

            <TextField
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value.slice(0, 80))}
              error={!titleValid}
              helperText={`${title.length}/80${titleValid ? '' : ' — title is required'}`}
              inputProps={{ maxLength: 80 }}
              size="small"
              fullWidth
            />
            <TextField
              label="Subtitle (optional)"
              value={subtitle}
              onChange={(event) => setSubtitle(event.target.value.slice(0, 140))}
              helperText={`${subtitle.length}/140`}
              inputProps={{ maxLength: 140 }}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
            <Typography variant="caption" color="text.secondary">
              Entity branding, active graph filters, and SoftwareIQ attribution are always included.
            </Typography>
          </Stack>

          <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Preview
            </Typography>
            <Box sx={{
              minHeight: isNarrow ? 410 : 540,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              p: 2,
            }}>
              <Box sx={{ width: preset.width * previewScale, height: preset.height * previewScale, flex: '0 0 auto' }}>
                <Box sx={{
                  width: preset.width,
                  height: preset.height,
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                }}>
                  <ChartShareFrame
                    frameRef={frameRef}
                    width={preset.width}
                    height={preset.height}
                    themeMode={themeMode}
                    ticker={resolvedTicker}
                    companyName={resolvedCompanyName}
                    entity={resolvedEntity}
                    title={title || ' '}
                    subtitle={subtitle}
                    sourceLabel={sourceLabel}
                    metadata={resolvedMetadata}
                    presentation={preset.presentation}
                    chartState={chartState}
                    renderChart={renderChart}
                    onReadyChange={setAssetReady}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={exporting}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<ImageOutlinedIcon />}
          onClick={handleDownload}
          disabled={!titleValid || !assetReady || exporting}
        >
          {exporting ? 'Creating PNG…' : 'Download PNG'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChartShareDialog;
